import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../../store/store';
import {
  DiscountResponse,
  fetchActiveDiscount,
} from '../../services/subscriptions';
import { SubscriptionAccess } from '../../types/Subscription';
import { selectAccessLevel } from '../subscriptions/subscriptionsSlice';

type DiscountState = {
  loading: boolean;
  error: string | null;
  data: DiscountResponse | null;
  lastFetchedAt: number | null;
  // Lowercased source name the user dismissed the banner for. The backend keeps
  // returning the account-level discount on every reload, so we persist this to
  // keep the banner hidden until a *different* discount source appears.
  dismissedSourceName: string | null;
};

const initialState: DiscountState = {
  loading: false,
  error: null,
  data: null,
  lastFetchedAt: null,
  dismissedSourceName: null,
};

// Thunk to load the active discount from backend
export const loadActiveDiscount = createAsyncThunk<
  DiscountResponse | null,
  void,
  { state: RootState }
>('discount/loadActiveDiscount', async () => {
  const shouldSkipDiscountFetchInDev =
    import.meta.env.DEV &&
    import.meta.env.VITE_ENABLE_DEV_DISCOUNT_FETCH !== 'true';

  if (shouldSkipDiscountFetchInDev) {
    return null;
  }

  try {
    const resp = await fetchActiveDiscount();
    if (resp && resp.hasDiscount) return resp;
    return null;
  } catch (e) {
    // Let reducer capture a generic error; upstream errors are logged by axios layer when relevant
    throw e as Error;
  }
});

const discountSlice = createSlice({
  name: 'discount',
  initialState,
  reducers: {
    clearDiscount(state) {
      state.data = null;
      state.error = null;
      state.lastFetchedAt = Date.now();
      state.loading = false;
    },
    // Dismiss the banner for the currently-shown discount source. We record the
    // source name (not just a boolean) so a genuinely new discount can still
    // surface later. Persisted via redux-persist so it survives reloads even
    // though `loadActiveDiscount` re-fetches the same discount.
    dismissDiscount(state, action: PayloadAction<string | null>) {
      state.dismissedSourceName = (action.payload || 'discount').toLowerCase();
      state.lastFetchedAt = Date.now();
    },
    setDiscount(state, action: PayloadAction<DiscountResponse | null>) {
      state.data = action.payload;
      state.error = null;
      state.lastFetchedAt = Date.now();
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadActiveDiscount.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loadActiveDiscount.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload;
        state.lastFetchedAt = Date.now();
      })
      .addCase(loadActiveDiscount.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error?.message || 'Failed to load discount';
      });
  },
});

export const { clearDiscount, setDiscount, dismissDiscount } =
  discountSlice.actions;

export const selectActiveDiscount = (state: RootState) => state.discount.data;
export const selectDismissedDiscountSourceName = (state: RootState) =>
  state.discount.dismissedSourceName;

// A discount whose free trial is at least this long counts as a claimable
// "free month" (30 days ≈ one month).
const CLAIM_FREE_MONTH_MIN_TRIAL_DAYS = 30;

/**
 * Whether to surface the "Claim Free Month" upsell. True when the user has an
 * active discount that also grants a ~30-day free trial and can still be claimed.
 *
 * The trial length lives on the discount's per-plan entries
 * (`plans[PLAN].trialPeriodDays`, sourced from the backend `DiscountRule`
 * override) — NOT on the top-level `discount` object, which only reflects the
 * Stripe coupon's recurrence (`durationType`/`durationMonths`). `plans` is only
 * populated when the offer has a valid coupon, so `hasDiscount` implies a real
 * discount is attached to the trial. `isTrialEligible` is `false` when the
 * backend won't grant the trial (a live billing relationship or elevated
 * access); it is absent on older responses, hence the local access-level check
 * below rather than a bare truthiness test.
 *
 * The access-level check is deliberately independent of `isTrialEligible`: the
 * discount slice is persisted, so a rehydrated payload predating the backend
 * flag would otherwise advertise a free month to someone who already has
 * access. Anyone above FREE — a paying subscriber, an active trialer (a
 * trialing subscription resolves to its product's level, not `TRIAL`),
 * institutional, one-time, or comped access — already has what this CTA sells.
 *
 * It is not a complete substitute for the backend flag. A paused subscriber is
 * still billable but resolves to FREE by design (access resolution ignores
 * paused rows), as do an `unpaid` subscription and an in-flight one-time trial.
 * Those only stop showing the CTA once `isTrialEligible` arrives, so on a stale
 * rehydrate they see it until `loadActiveDiscount` resolves.
 */
export const selectShowClaimFreeMonth = (state: RootState): boolean => {
  const active = state.discount.data;
  if (!active?.hasDiscount) return false;
  if (active.isTrialEligible === false) return false;
  if (selectAccessLevel(state) !== SubscriptionAccess.FREE) return false;

  const plans = active.plans;
  if (!plans) return false;

  return Object.values(plans).some(
    (plan) =>
      plan?.trialPeriodDays != null &&
      plan.trialPeriodDays >= CLAIM_FREE_MONTH_MIN_TRIAL_DAYS
  );
};

export default discountSlice.reducer;
